import { useState } from 'react';
import { toast } from 'sonner';
import { api, formatApiError, resolveImage } from '@/lib/api';
import { useCart } from '@/context/CartContext';

export function CartDrawer() {
  const { items, setQty, remove, clear, subtotal, open, setOpen } = useCart();
  const [checkingOut, setCheckingOut] = useState(false);

  if (!open) return null;

  const checkout = async () => {
    if (items.length === 0) return;
    setCheckingOut(true);
    try {
      const { data } = await api.post('/payments/checkout', {
        items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setCheckingOut(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70]" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-yunti-ink/50" onClick={() => setOpen(false)} aria-hidden="true" />
      <aside className="absolute right-0 top-0 h-full w-full max-w-md bg-white border-l border-yunti-ink/10 flex flex-col">
        <header className="flex items-center justify-between px-6 py-4 border-b border-yunti-ink/10">
          <h2 className="font-display uppercase tracking-tight text-xl">Cart</h2>
          <button
            onClick={() => setOpen(false)}
            className="text-xs font-mono uppercase tracking-widest text-yunti-ink/60 hover:text-yunti-ink"
            data-testid="cart-close"
          >
            Close ×
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {items.length === 0 && <p className="text-sm text-yunti-ink/50">Your cart is empty.</p>}
          {items.map((i) => (
            <div key={i.id} className="flex gap-4 items-center border-b border-yunti-ink/10 pb-4" data-testid={`cart-item-${i.id}`}>
              <div className="w-16 h-20 bg-yunti-sand border border-yunti-ink/10 overflow-hidden shrink-0">
                {i.image && <img src={resolveImage(i.image)} alt={i.name} className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1">
                <p className="font-display uppercase tracking-tight">{i.name}</p>
                <p className="text-sm font-mono text-yunti-ink/70">${Number(i.price).toFixed(2)}</p>
                <div className="flex items-center gap-3 mt-2">
                  <div className="flex items-center border border-yunti-ink/20">
                    <button onClick={() => setQty(i.id, i.quantity - 1)} className="px-2 py-1 text-sm" data-testid={`cart-dec-${i.id}`}>−</button>
                    <span className="px-3 text-sm font-mono">{i.quantity}</span>
                    <button onClick={() => setQty(i.id, i.quantity + 1)} className="px-2 py-1 text-sm" data-testid={`cart-inc-${i.id}`}>+</button>
                  </div>
                  <button onClick={() => remove(i.id)} className="text-[11px] font-mono uppercase text-red-700 hover:underline" data-testid={`cart-remove-${i.id}`}>
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          <footer className="px-6 py-4 border-t border-yunti-ink/10 space-y-3">
            <div className="flex justify-between text-sm font-mono">
              <span>Subtotal</span>
              <span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
            </div>
            <p className="text-[11px] text-yunti-ink/50">Taxes calculated at checkout. Shipping TBD.</p>
            <button
              onClick={checkout}
              disabled={checkingOut}
              className="w-full py-4 bg-yunti-ink text-white text-xs font-mono uppercase tracking-[0.2em] hover:bg-yunti-purple transition-colors font-semibold disabled:opacity-60"
              data-testid="cart-checkout-btn"
            >
              {checkingOut ? 'Opening checkout…' : 'Checkout'}
            </button>
            <button onClick={clear} className="w-full text-[11px] font-mono uppercase tracking-wider text-yunti-ink/50 hover:text-yunti-ink" data-testid="cart-clear">
              Clear cart
            </button>
          </footer>
        )}
      </aside>
    </div>
  );
}
